import { Badge } from "@themesberg/react-bootstrap";
import React, { useState } from "react";
import ImagePreview from "../../ticket/sub-screen/image-preview/ImagePreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { getSaleStatus, getReturnStatus } from "../helpers/sale-status";

const SalesPairTicketRow = (props) => {
  const { pairGroup, selectedTicketIds, onSelectTicket, onShowDetail } = props;
  const { last5Numbers, representative, tickets } = pairGroup;

  const [expanded, setExpanded] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const displayNumbers = `* ${(last5Numbers || "").split("").join(" ")}`;
  const totalAmount = tickets.reduce((sum, t) => sum + t.amount, 0);
  const totalInputAmount = tickets.reduce(
    (sum, t) => sum + (t.inputAmount || 0),
    0,
  );
  const totalSoldAmount = tickets.reduce(
    (sum, t) => sum + (t.soldAmount || 0),
    0,
  );
  const totalReturnedAmount = tickets.reduce(
    (sum, t) => sum + (t.returnedAmount || 0),
    0,
  );
  const hasExpiredTickets = tickets.some((t) => t.isExpired);
  const images = representative.images;
  const groupSaleStatus = getSaleStatus({
    status: representative.status,
    amount: totalAmount,
    isExpired: hasExpiredTickets,
  });

  // Count how many tickets from this group are not fully returned (can still return more)
  const returnableTickets = tickets.filter(
    (t) =>
      Number(t.status) === 1 &&
      Number(t.amount) > 0 &&
      (!t.returnedAmount || t.returnedAmount < t.inputAmount),
  );
  const selectedInGroup = returnableTickets.filter((t) =>
    selectedTicketIds?.includes(t.id),
  ).length;
  const allSelectedInGroup =
    returnableTickets.length > 0 &&
    selectedInGroup === returnableTickets.length;
  const someSelectedInGroup =
    selectedInGroup > 0 && selectedInGroup < returnableTickets.length;

  const groupReturnStatus = getReturnStatus({
    returnedAmount: totalReturnedAmount,
    inputAmount: totalInputAmount,
  });

  const openImagePreview = (img, title) => {
    setPreviewImage(img);
    setPreviewTitle(title);
    setShowImagePreview(true);
  };

  const getImageSrc = (img) => {
    if (!img) return null;
    return typeof img === "string" && img.startsWith("http")
      ? img
      : `data:image/jpeg;base64,${img}`;
  };

  const handleGroupCheckboxChange = (checked) => {
    returnableTickets.forEach((ticket) => {
      onSelectTicket && onSelectTicket(ticket.id, checked, ticket);
    });
  };

  return (
    <>
      <tr
        className={`align-middle ${expanded ? "bg-light" : ""}`}
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="text-center py-3" onClick={(e) => e.stopPropagation()}>
          <div className="form-check d-flex justify-content-center">
            <input
              className="form-check-input"
              type="checkbox"
              checked={allSelectedInGroup}
              disabled={returnableTickets.length === 0}
              ref={(input) => {
                if (input) input.indeterminate = someSelectedInGroup;
              }}
              onChange={(e) => handleGroupCheckboxChange(e.target.checked)}
              style={{ cursor: "pointer", transform: "scale(1.2)" }}
            />
          </div>
        </td>

        <td
          className="text-start fw-bold text-primary text-truncate"
          style={{
            maxWidth: "120px",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onShowDetail &&
              onShowDetail({
                ...representative,
                isPairGroup: true,
                ticketCount: tickets.length,
                displayNumbers: `* ${(last5Numbers || "").split("").join(" ")}`,
                inputAmount: totalInputAmount,
                soldAmount: totalSoldAmount,
                amount: totalAmount,
                returnedAmount: totalReturnedAmount,
                isExpired: hasExpiredTickets,
              });
          }}
        >
          #{representative.id}
        </td>

        <td className="text-center">
          <Badge bg="warning" text="dark" className="px-3 py-2 fw-normal">
            Vé cặp nguyên
          </Badge>
          <div className="small text-muted mt-1">{tickets.length} vé</div>
        </td>

        <td className="text-start">
          <div className="d-flex align-items-center gap-2">
            {representative.channelLogo && (
              <img
                src={representative.channelLogo}
                alt={representative.channelName}
                className="rounded-circle border"
                style={{ width: "36px", height: "36px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div className="text-start">
              <div className="fw-bolder text-dark">
                {representative.channelName}
              </div>
              <div className="small text-muted">
                {representative.resultDate}
              </div>
            </div>
          </div>
        </td>

        <td className="text-center">
          <Badge
            bg=""
            className="fs-6 font-monospace bg-light text-dark border border-gray-200 px-3 py-2"
            style={{ letterSpacing: "2px" }}
          >
            {displayNumbers}
          </Badge>
        </td>

        <td className="text-center">
          <span className="fw-bold text-primary">
            {new Intl.NumberFormat("vi-VN").format(totalInputAmount)}
          </span>
        </td>

        <td className="text-center">
          <span className="fw-bold text-success">
            {new Intl.NumberFormat("vi-VN").format(totalSoldAmount)}
          </span>
        </td>

        <td className="text-center">
          <span
            className={`fw-bold ${hasExpiredTickets ? "text-danger" : "text-warning"}`}
          >
            {new Intl.NumberFormat("vi-VN").format(totalAmount)}
          </span>
        </td>

        <td className="text-center">
          <span className="fw-bold text-danger">
            {new Intl.NumberFormat("vi-VN").format(totalReturnedAmount)}
          </span>
        </td>

        <td className="text-center">
          <div className="d-flex flex-column align-items-center gap-1">
            <Badge bg={groupSaleStatus.variant} className="px-3 py-2 fw-normal">
              {groupSaleStatus.text}
            </Badge>
            {!hasExpiredTickets &&
              (groupSaleStatus.text === "Đang bán" ||
                groupSaleStatus.text === "Hết vé") && (
                <span className="text-muted small">
                  Còn {new Intl.NumberFormat("vi-VN").format(totalAmount)} vé
                </span>
              )}
          </div>
        </td>

        <td className="text-center">
          <Badge bg={groupReturnStatus.variant} className="px-3 py-2 fw-normal">
            {groupReturnStatus.text}
          </Badge>
        </td>

        <td className="text-center">
          {images ? (
            <div
              className="position-relative d-inline-block rounded overflow-hidden shadow-sm"
              style={{ width: "40px", height: "40px", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                openImagePreview(images, `Vé cặp *${last5Numbers}`);
              }}
            >
              <img
                src={getImageSrc(images)}
                alt="Ticket"
                className="w-100 h-100 object-fit-cover"
              />
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-black bg-opacity-25 hover-overlay">
                <FontAwesomeIcon icon={faImages} className="text-white small" />
              </div>
            </div>
          ) : (
            <span className="text-muted small">-</span>
          )}
        </td>

        <td className="text-center text-muted small">
          {moment(representative.createdAt || "").format("DD/MM HH:mm")}
        </td>

        <td className="text-center">
          {representative.createdByName ? (
            <span className="small text-dark">
              {representative.createdByName}
            </span>
          ) : (
            <span className="text-muted small">-</span>
          )}
        </td>

        <td className="text-center">
          {representative.approvedByName ? (
            <span className="small text-success">
              {representative.approvedByName}
            </span>
          ) : (
            <span className="text-muted small">-</span>
          )}
        </td>
      </tr>

      {expanded &&
        tickets.map((ticket) => {
          const canTicketBeReturned =
            Number(ticket.status) === 1 &&
            Number(ticket.amount) > 0 &&
            (!ticket.returnedAmount ||
              ticket.returnedAmount < ticket.inputAmount);
          const isTicketSelected = selectedTicketIds?.includes(ticket.id);
          const ticketSaleStatus = getSaleStatus({
            status: ticket.status,
            amount: ticket.amount,
            isExpired: ticket.isExpired,
          });
          const ticketReturnStatus = getReturnStatus({
            returnedAmount: ticket.returnedAmount,
            inputAmount: ticket.inputAmount,
          });

          return (
            <tr
              key={ticket.id}
              className={isTicketSelected ? "bg-light-yellow" : "bg-light"}
            >
              <td className="text-center py-2">
                <div className="form-check d-flex justify-content-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isTicketSelected}
                    disabled={!canTicketBeReturned}
                    onChange={(e) =>
                      onSelectTicket &&
                      onSelectTicket(ticket.id, e.target.checked, ticket)
                    }
                    style={{
                      cursor: canTicketBeReturned ? "pointer" : "not-allowed",
                      transform: "scale(1.1)",
                    }}
                  />
                </div>
              </td>

              <td
                className="text-start ps-3 text-dark narrow-font small fw-bold"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onShowDetail && onShowDetail(ticket);
                }}
              >
                #{ticket.id}
              </td>

              <td className="text-center">
                <Badge bg="secondary" className="px-3 py-2 fw-normal">
                  Con
                </Badge>
              </td>

              <td className="text-start small text-dark fw-bold">
                {ticket.channelName}
              </td>

              <td className="text-center">
                <Badge
                  bg=""
                  className="fs-6 font-monospace bg-white text-dark border border-light px-2 py-1"
                  style={{ letterSpacing: "2px" }}
                >
                  {(ticket.numbers || "").split("").join(" ")}
                </Badge>
              </td>

              <td className="text-center">
                <span className="fw-bold text-primary small">
                  {new Intl.NumberFormat("vi-VN").format(
                    ticket.inputAmount || 0,
                  )}
                </span>
              </td>

              <td className="text-center">
                <span className="fw-bold text-success small">
                  {new Intl.NumberFormat("vi-VN").format(
                    ticket.soldAmount || 0,
                  )}
                </span>
              </td>

              <td className="text-center">
                <span
                  className={`fw-bold small ${ticket.isExpired && ticket.amount > 0 ? "text-danger" : "text-warning"}`}
                >
                  {new Intl.NumberFormat("vi-VN").format(ticket.amount || 0)}
                </span>
              </td>

              <td className="text-center">
                <span className="fw-bold text-danger small">
                  {new Intl.NumberFormat("vi-VN").format(
                    ticket.returnedAmount || 0,
                  )}
                </span>
              </td>

              <td className="text-center">
                <div className="d-flex flex-column align-items-center gap-1">
                  <Badge
                    bg={ticketSaleStatus.variant}
                    className="px-2 py-1 small fw-normal"
                  >
                    {ticketSaleStatus.text}
                  </Badge>
                  {!ticket.isExpired &&
                    (ticketSaleStatus.text === "Đang bán" ||
                      ticketSaleStatus.text === "Hết vé") && (
                      <span className="text-muted small">
                        Còn{" "}
                        {new Intl.NumberFormat("vi-VN").format(
                          ticket.amount || 0,
                        )}
                      </span>
                    )}
                </div>
              </td>

              <td className="text-center">
                <Badge
                  bg={ticketReturnStatus.variant}
                  className="px-2 py-1 small fw-normal"
                >
                  {ticketReturnStatus.text}
                </Badge>
              </td>

              <td className="text-center">
                {ticket.images && (
                  <div
                    className="d-inline-block rounded overflow-hidden border"
                    style={{ width: "32px", height: "32px", cursor: "pointer" }}
                    onClick={() =>
                      openImagePreview(
                        ticket.images,
                        `Vé đơn ${ticket.numbers}`,
                      )
                    }
                  >
                    <img
                      src={getImageSrc(ticket.images)}
                      alt="Ticket"
                      className="w-100 h-100 object-fit-cover"
                    />
                  </div>
                )}
              </td>

              <td className="text-center text-muted small">-</td>

              <td className="text-center">
                {ticket.createdByName ? (
                  <span className="small text-dark">
                    {ticket.createdByName}
                  </span>
                ) : (
                  <span className="text-muted small">-</span>
                )}
              </td>

              <td className="text-center">
                {ticket.approvedByName ? (
                  <span className="small text-success">
                    {ticket.approvedByName}
                  </span>
                ) : (
                  <span className="text-muted small">-</span>
                )}
              </td>
            </tr>
          );
        })}

      <ImagePreview
        show={showImagePreview}
        onHide={() => setShowImagePreview(false)}
        image={previewImage}
        title={previewTitle}
      />
    </>
  );
};

export default React.memo(SalesPairTicketRow);
